import React, { useId, useRef, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import type { ActivePopupModel } from '@pages/administration/announcements/announcements.models';
import { PopupType } from '@pages/administration/announcements/announcements.models';
import { AnnouncementPopupProps, useAnnouncementPopup } from './useAnnouncementPopup';
import './AnnouncementPopup.scss';

interface TypeConfig {
    icon: IconDefinition;
    cssClass: string;
}

const TYPE_CONFIG: Record<PopupType, TypeConfig> = {
    [PopupType.Information]: { icon: icons.faInfoCircle,         cssClass: 'announcement-popup--information' },
    [PopupType.Warning]:     { icon: icons.faExclamationTriangle, cssClass: 'announcement-popup--warning'     },
    [PopupType.Alert]:       { icon: icons.faExclamationCircle,  cssClass: 'announcement-popup--alert'       },
};

export default function AnnouncementPopup(props: AnnouncementPopupProps) {
    const { state, handlers } = useAnnouncementPopup(props);
    const { t } = useTranslation();
    const titleId = useId();
    const closeRef = useRef<HTMLButtonElement>(null);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (state.current) closeRef.current?.focus();
    }, [state.current?.Guid]);

    useEffect(() => {
        setIsClosing(false);
    }, [state.current?.Guid]);

    useEffect(() => {
        if (!isClosing) return;
        const timer = setTimeout(() => handlers.handleClose(), 200);
        return () => clearTimeout(timer);
    }, [isClosing]);

    if (!state.current) return null;

    const config = TYPE_CONFIG[state.current.PopupTypeId] ?? TYPE_CONFIG[PopupType.Information];
    const closingClass = isClosing ? '--closing' : '';

    return (
        <div className={`announcement-backdrop${closingClass ? ` announcement-backdrop${closingClass}` : ''}`} aria-hidden="false">
            <div
                className={`announcement-popup ${config.cssClass}${closingClass ? ` announcement-popup${closingClass}` : ''}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div className="announcement-popup__header">
                    <FontAwesomeIcon icon={config.icon} className="announcement-popup__icon" aria-hidden="true" />
                    <h2 id={titleId} className="announcement-popup__title">
                        {state.current.Title}
                    </h2>
                    <button
                        ref={closeRef}
                        className="announcement-popup__close"
                        onClick={() => setIsClosing(true)}
                        aria-label={t('ANNOUNCEMENT_Close')}
                    >
                        &times;
                    </button>
                </div>
                <div
                    className="announcement-popup__body"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(state.current.HtmlContent) }}
                />
            </div>
        </div>
    );
}
